import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useComments } from '../../hooks/useComments';
import { enrichConfigSectionsWithComments } from '../../utils/commentUtils';
import { 
  Card, Tabs, Form, Input, Switch, InputNumber, Button, message, Space, 
  Typography, Row, Col, Spin, Select, Alert, Modal,
  Tag, Popconfirm, Tooltip, Input as AntInput
} from 'antd';
// import { FixedSizeList as List } from 'react-window';
import { 
  SaveOutlined, ReloadOutlined, FileTextOutlined, EyeOutlined, 
  PlusOutlined, EditOutlined, DeleteOutlined,
  SettingOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;

// 移除固定CSS样式，改为使用主题支持的CSS类

// 移除防抖函数，提升输入响应性

interface Parameter {
  key: string;
  value: any;
  type: 'string' | 'number' | 'boolean';
  description?: string;
  comment?: string;
}

interface CommonParameter {
  parameter_key: string;
  parameter_comment: string;
  parameter_description: string;
  config_section: string;
  config_file: string;
  sort_order: number;
  is_default: number; // 1=默认, 0=不是默认
}

interface Section {
  name: string;
  description: string;
  parameters: { [key: string]: Parameter };
}

interface ConfigStructure {
  maps: { [key: string]: string };
  sections: {
    gameUserSettings: { [key: string]: Section };
    gameIni: { [key: string]: Section };
  };
}

interface ARKConfigManagerProps {
  serverId: string;
}

// 参数输入控件组件（移到组件外部避免重新创建）
const ParameterInput = React.memo(({ section, param, onUpdate, isAdmin }: { 
  section: string; 
  param: Parameter; 
  onUpdate: (section: string, key: string, value: any) => void;
  isAdmin: boolean;
}) => {
  const { key, value, type, description, comment } = param;
  
  // 普通用户只读字段
  const readOnlyFields = ['RCONPort', 'MaxPlayers', 'Port', 'QueryPort'];
  const isReadOnly = !isAdmin && readOnlyFields.includes(key);
  
  // 使用 useRef 存储所有值，完全避免重新渲染
  const inputRef = useRef<any>(null);
  const onUpdateRef = useRef(onUpdate);
  const sectionRef = useRef(section);
  const keyRef = useRef(key);
  
  // 更新 ref 值
  onUpdateRef.current = onUpdate;
  sectionRef.current = section;
  keyRef.current = key;
  
  // 使用本地状态来避免输入法中断
  const [localValue, setLocalValue] = useState(value);
  
  // 同步外部值变化到本地状态
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  // 完全稳定的处理函数，不依赖任何外部状态
  const handleChange = useCallback((newValue: any) => {
    onUpdateRef.current(sectionRef.current, keyRef.current, newValue);
  }, []);
  
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onUpdateRef.current(sectionRef.current, keyRef.current, newValue);
  }, []);
  
  switch (type) {
    case 'boolean':
      return (
        <Switch
          ref={inputRef}
          checked={Boolean(localValue)}
          onChange={handleChange}
          disabled={isReadOnly}
        />
      );
    case 'number':
      return (
        <InputNumber
          ref={inputRef}
          value={localValue}
          onChange={handleChange}
          className="ark-config-manager-full-width"
          disabled={isReadOnly}
        />
      );
    default:
      // 为Message添加特殊处理，使用TextArea支持多行中文输入
      // SessionName使用单行输入框
      const isMessageField = key === 'Message';
      const isChineseField = key === 'SessionName' || key === 'Message';
      
      if (isMessageField) {
        return (
          <Input.TextArea
            ref={inputRef}
            value={localValue || ''}
            onChange={handleInputChange}
            placeholder={`请输入${key}`}
            allowClear
            maxLength={200}
            showCount
            rows={3}
            readOnly={isReadOnly}
            className={`ark-config-manager-textarea ${isReadOnly ? 'ark-config-manager-readonly' : ''}`}
          />
        );
      }
      
      return (
        <Input
          ref={inputRef}
          value={localValue || ''}
          onChange={handleInputChange}
          placeholder={`请输入${key}`}
          allowClear
          maxLength={isChineseField ? 100 : undefined}
          showCount={isChineseField}
          readOnly={isReadOnly}
          className={`ark-config-manager-input ${isChineseField ? 'ark-config-manager-chinese' : ''} ${isReadOnly ? 'ark-config-manager-readonly' : ''}`}
        />
      );
  }
});

const ARKConfigManager: React.FC<ARKConfigManagerProps> = ({ serverId }) => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configStructure, setConfigStructure] = useState<ConfigStructure | null>(null);
  const [currentConfig, setCurrentConfig] = useState<any>({});
  const [commandPreview, setCommandPreview] = useState<string>('');
  const [configFilesPreview, setConfigFilesPreview] = useState<{gameUserSettings: string, gameIni: string}>({gameUserSettings: '', gameIni: ''});
  const [activeTab, setActiveTab] = useState('gameUserSettings');
  // 使用统一的注释管理系统
  const { findComment, isInitialized: commentsReady } = useComments({ 
    gameTypeId: 4, // ARK的游戏类型ID
    autoLoad: true 
  });
  const [gameTypeId, setGameTypeId] = useState<number | null>(4);
  const [commonParameters, setCommonParameters] = useState<CommonParameter[]>([]);
  const [showCommonParameters, setShowCommonParameters] = useState(true);
  const [addCommonParamModalVisible, setAddCommonParamModalVisible] = useState(false);
  const [newCommonParam, setNewCommonParam] = useState({
    parameterKey: '',
    parameterComment: '',
    parameterDescription: '',
    configSection: '',
    configFile: 'GameUserSettings'
  });
  const [selectedParameters, setSelectedParameters] = useState<string[]>([]);
  const [availableParameters, setAvailableParameters] = useState<Array<{
    key: string;
    section: string;
    file: string;
    comment: string;
    description: string;
  }>>([]);
  
  // 搜索和显示状态
  const [searchText, setSearchText] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showOnlyWithParams, setShowOnlyWithParams] = useState(false);
  const [addParamModalVisible, setAddParamModalVisible] = useState(false);
  const [editParamModalVisible, setEditParamModalVisible] = useState(false);
  const [editingSection, setEditingSection] = useState<string>('');
  const [editingParamKey, setEditingParamKey] = useState<string>('');
  const [newParam, setNewParam] = useState<{key: string, value: any, type: string, description: string, comment: string}>({
    key: '',
    value: '',
    type: 'string',
    description: '',
    comment: ''
  });
  const [editParam, setEditParam] = useState<{key: string, value: any, type: string, description: string, comment: string}>({
    key: '',
    value: '',
    type: 'string',
    description: '',
    comment: ''
  });

  // 检查是否为管理员
  const isAdmin = user?.role === 'admin';

  // 加载配置结构
  const loadConfigStructure = async () => {
    try {
      const response = await api.get('/ark/config-structure-public');
      setConfigStructure(response.data.data);
    } catch (error) {
      console.error('加载ARK配置结构失败:', error);
      message.error('加载ARK配置结构失败');
    }
  };

  // 注释加载现在由useComments Hook自动处理

  // 加载常用参数
  const loadCommonParameters = async () => {
    try {
      let currentGameTypeId = gameTypeId;
      if (!currentGameTypeId) {
        const serverResponse = await api.get(`/servers/${serverId}`);
        currentGameTypeId = serverResponse.data.game_type_id;
        setGameTypeId(currentGameTypeId);
      }
      const response = await api.get(`/ark/common-parameters/${currentGameTypeId}`);
      setCommonParameters(response.data.data || []);
    } catch (error: any) {
      console.error('❌ [ARK] 加载常用参数失败:', error);
      console.error('❌ [ARK] 错误详情:', error.response?.data || error.message);
      console.error('❌ [ARK] 错误状态码:', error.response?.status);
      message.error(`加载常用参数失败: ${error.response?.data?.message || error.message}`);
    }
  };


  // 合并配置结构和实际配置数据（使用统一的注释系统）
  const mergeConfigWithStructure = (configStructure: any, configData: any) => {
    const mergedStructure = JSON.parse(JSON.stringify(configStructure));
    
    // 合并GameUserSettings
    if (configData.gameUserSettings && mergedStructure.sections.gameUserSettings) {
      for (const [sectionKey, sectionData] of Object.entries(configData.gameUserSettings)) {
        if (mergedStructure.sections.gameUserSettings[sectionKey]) {
          // 使用统一的注释系统为配置数据添加注释
          const enrichedData = enrichConfigSectionsWithComments(
            { [sectionKey]: sectionData }, 
            'GameUserSettings'
          );
          mergedStructure.sections.gameUserSettings[sectionKey].parameters = enrichedData[sectionKey];
        }
      }
    }
    
    // 合并GameIni
    if (configData.gameIni && mergedStructure.sections.gameIni) {
      for (const [sectionKey, sectionData] of Object.entries(configData.gameIni)) {
        if (mergedStructure.sections.gameIni[sectionKey]) {
          // 使用统一的注释系统为配置数据添加注释
          const enrichedData = enrichConfigSectionsWithComments(
            { [sectionKey]: sectionData }, 
            'GameIni'
          );
          mergedStructure.sections.gameIni[sectionKey].parameters = enrichedData[sectionKey];
        }
      }
    }
    
    return mergedStructure;
  };

  // 加载服务器配置
  const loadServerConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/ark/config/${serverId}`);
      
      const configData = response.data.data;
      
      // 静默加载配置数据
      setCurrentConfig(configData);
      form.setFieldsValue(configData);
      
      // 不自动生成预览，用户需要时手动点击"生成预览"按钮
      message.success('配置重新加载成功');
      
    } catch (error) {
      console.error('加载ARK配置失败:', error);
      message.error('加载ARK配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 合并配置结构和实际数据（使用useMemo优化）
  const mergedConfigStructure = useMemo(() => {
    if (configStructure && currentConfig && Object.keys(currentConfig).length > 0 && commentsReady) {
      return mergeConfigWithStructure(configStructure, currentConfig);
    }
    return configStructure;
  }, [configStructure, currentConfig, commentsReady]);

  // 获取可用参数列表（优化版本）
  const loadAvailableParameters = useCallback(() => {
    if (!mergedConfigStructure) return;
    
    // 使用更高效的方式构建参数列表
    const params: Array<{
      key: string;
      section: string;
      file: string;
      comment: string;
      description: string;
    }> = [];
    
    // 遍历所有配置项
    for (const [fileKey, file] of Object.entries(mergedConfigStructure.sections)) {
      for (const [sectionKey, section] of Object.entries(file as any)) {
        if ((section as any).parameters) {
          for (const [paramKey, param] of Object.entries((section as any).parameters as any)) {
            // 调试：检查参数注释的来源
            const paramComment = (param as any).comment;
            const paramValue = (param as any).value;
            
            // 如果注释包含参数值，则使用参数键作为注释
            const finalComment = (paramComment && paramComment !== paramValue) ? paramComment : paramKey;
            
            // 调试日志
            if (paramComment && paramComment !== paramKey && paramComment !== paramValue) {
              console.log(`🔍 参数注释调试 - ${paramKey}:`, {
                originalComment: paramComment,
                paramValue: paramValue,
                finalComment: finalComment
              });
            }
            
            params.push({
              key: paramKey,
              section: sectionKey,
              file: fileKey === 'gameUserSettings' ? 'GameUserSettings' : 'GameIni',
              comment: finalComment,
              description: (param as any).description || paramKey
            });
          }
        }
      }
    }
    
    setAvailableParameters(params);
  }, [mergedConfigStructure]);

  // 移除可能导致无限循环的 setConfigStructure 调用
  // mergedConfigStructure 已经通过 useMemo 计算，不需要重新设置到状态中

  // 智能搜索和过滤逻辑（优化版本）
  const filteredConfigStructure = useMemo(() => {
    if (!mergedConfigStructure) return null;
    
    // 如果没有搜索文本，直接返回原始结构
    if (!searchText.trim()) {
      return mergedConfigStructure;
    }
    
    const filtered = JSON.parse(JSON.stringify(mergedConfigStructure));
    const searchLower = searchText.toLowerCase();
    
    // 过滤每个分组
    for (const [tabKey, tab] of Object.entries(filtered.sections)) {
      for (const [sectionKey, section] of Object.entries(tab as any)) {
        if ((section as any).parameters) {
          const filteredParams: any = {};
          for (const [paramKey, param] of Object.entries((section as any).parameters as any)) {
            if (paramKey.toLowerCase().includes(searchLower) ||
                ((param as any).description && (param as any).description.toLowerCase().includes(searchLower))) {
              filteredParams[paramKey] = param;
            }
          }
          (section as any).parameters = filteredParams;
        }
      }
    }
    
    return filtered;
  }, [mergedConfigStructure, searchText]);

  // 获取当前标签页的扁平化参数列表（用于虚拟滚动）
  const flattenedParams = useMemo(() => {
    if (!filteredConfigStructure) return [];
    
    const sections = filteredConfigStructure.sections[activeTab] || {};
    const params: Array<{sectionKey: string, section: any, paramKey: string, param: any}> = [];
    
    // 生成扁平化参数列表
    
    Object.keys(sections).forEach(sectionKey => {
      const section = sections[sectionKey];
      if (section.parameters) {
        Object.keys(section.parameters).forEach(paramKey => {
          const param = section.parameters[paramKey];
          // 处理参数
          params.push({
            sectionKey,
            section,
            paramKey,
            param
          });
        });
      }
    });
    
    // 扁平化参数列表生成完成
    return params;
  }, [filteredConfigStructure, activeTab]);

  // 获取过滤后的分组信息
  const filteredSections = useMemo(() => {
    if (!filteredConfigStructure) return [];
    
    const sections = filteredConfigStructure.sections[activeTab] || {};
    const sectionList: Array<{key: string, section: any, paramCount: number, hasParams: boolean}> = [];
    
    Object.keys(sections).forEach(sectionKey => {
      const section = sections[sectionKey];
      const paramCount = section.parameters ? Object.keys(section.parameters).length : 0;
      const hasParams = paramCount > 0;
      
      // 如果设置了只显示有参数的分组，则过滤
      if (!showOnlyWithParams || hasParams) {
        sectionList.push({
          key: sectionKey,
          section: {
            ...section,
            paramCount,
            hasParams
          },
          paramCount,
          hasParams
        });
      }
    });
    
    return sectionList;
  }, [filteredConfigStructure, activeTab, showOnlyWithParams]);

  // 生成启动命令预览（直接调用，无防抖）
  const generateCommandPreview = React.useCallback(
    async (config: any) => {
      try {
        const response = await api.post('/ark/generate-command-public', {
          config,
          mapName: 'TheIsland'
        });
        setCommandPreview(response.data.command);
      } catch (error) {
        console.error('生成启动命令失败:', error);
      }
    },
    []
  );

  // 生成配置文件预览（使用useCallback避免无限循环）
  const generateConfigFilesPreview = useCallback(async (config: any) => {
    try {
      const response = await api.post('/ark/generate-config-files-public', {
        config
      });
      setConfigFilesPreview(response.data.data);
    } catch (error) {
      console.error('生成配置文件预览失败:', error);
      message.error('生成配置文件预览失败');
    }
  }, []);

  // 生成配置文件预览（直接调用，无防抖）
  const generateConfigFilesPreviewDebounced = useCallback(
    async (config: any) => {
      await generateConfigFilesPreview(config);
    },
    [generateConfigFilesPreview]
  );

  // 移除自动生成预览，让用户手动控制

  // 保存配置
  const saveConfig = async () => {
    try {
      setSaving(true);
      
      // 使用 currentConfig 而不是 form.validateFields()
      const response = await api.post(`/ark/config/${serverId}`, {
        config: currentConfig
      });
      message.success('配置保存成功');
      
      // 保存成功后异步生成预览，不阻塞保存流程
      setTimeout(() => {
        generateCommandPreview(currentConfig);
        generateConfigFilesPreview(currentConfig);
      }, 100);
      
      // 不重新加载配置，避免额外的API调用
      // await loadServerConfig();
      
    } catch (error) {
      console.error('保存配置失败:', error);
      message.error('保存配置失败');
    } finally {
      setSaving(false);
    }
  };

  // 添加新参数
  const addParameter = async () => {
    if (!newParam.key.trim()) {
      message.error('请输入参数名称');
      return;
    }

    const updatedConfig = { ...currentConfig };
    if (!updatedConfig[activeTab]) {
      updatedConfig[activeTab] = {};
    }
    if (!updatedConfig[activeTab][editingSection]) {
      updatedConfig[activeTab][editingSection] = {};
    }

    // 转换值类型
    let value = newParam.value;
    if (newParam.type === 'number') {
      value = parseFloat(newParam.value) || 0;
    } else if (newParam.type === 'boolean') {
      value = newParam.value === 'true' || newParam.value === true;
    }

    updatedConfig[activeTab][editingSection][newParam.key] = {
      key: newParam.key,
      value: value,
      type: newParam.type,
      description: newParam.description,
      comment: newParam.comment
    };

    setCurrentConfig(updatedConfig);
    form.setFieldsValue(updatedConfig);
    setAddParamModalVisible(false);
    setNewParam({ key: '', value: '', type: 'string', description: '', comment: '' });

    // 保存参数注释到数据库
    try {
      if (gameTypeId) {
        const configFile = activeTab === 'gameUserSettings' ? 'GameUserSettings' : 'GameIni';
        await api.post(`/ark/parameter-comments`, {
          gameTypeId: gameTypeId,
          parameterKey: newParam.key,
          parameterComment: newParam.comment,
          parameterDescription: newParam.description,
          configSection: editingSection,
          configFile: configFile
        });
        console.log('✅ 参数注释保存成功');
      }
    } catch (error) {
      console.error('❌ 保存参数注释失败:', error);
      // 不显示错误消息，因为参数本身已经保存成功
    }

    message.success('参数添加成功');
  };

  // 编辑参数
  const editParameter = async (section: string, key: string) => {
    const param = currentConfig[activeTab]?.[section]?.[key];
    if (param) {
      setEditingSection(section);
      setEditingParamKey(key);
      
      // 从数据库加载该参数的注释和描述
      let dbComment = '';
      let dbDescription = '';
      
      try {
        if (gameTypeId) {
          const configFile = activeTab === 'gameUserSettings' ? 'GameUserSettings' : 'GameIni';
          const response = await api.get(`/ark/parameter-comments/${gameTypeId}`);
          const comments = response.data.data || {};
          
          // 尝试多种键格式来查找注释
          const possibleKeys = [
            `${configFile}_${section}_${key}`,
            `${configFile}_ServerSettings_${key}`,
            `${configFile}_/Script/ShooterGame.ShooterGameUserSettings_${key}`,
            `${configFile}_/Script/Engine.HUD_${key}`,
            `${configFile}_/Script/EngineSettings.GeneralProjectSettings_${key}`,
            `${configFile}_/Script/ShooterGame.ShooterGameMode_${key}`
          ];
          
          let commentData = null;
          for (const commentKey of possibleKeys) {
            if (comments[commentKey]) {
              commentData = comments[commentKey];
              // 找到注释
              break;
            }
          }
          
          if (commentData) {
            dbComment = commentData.comment || '';
            dbDescription = commentData.description || '';
          } else {
            // 未找到注释
          }
        }
      } catch (error) {
        console.error('❌ [ARK] 加载参数注释失败:', error);
        // 如果加载失败，使用当前配置中的值作为备选
      }
      
      setEditParam({
        key: param.key || key,
        value: param.value,
        type: param.type || 'string',
        description: dbDescription || param.description || '',
        comment: dbComment || param.comment || ''
      });
      setEditParamModalVisible(true);
    }
  };

  // 保存编辑的参数
  const saveEditParameter = async () => {
    if (!editParam.key.trim()) {
      message.error('请输入参数名称');
      return;
    }

    const updatedConfig = { ...currentConfig };
    if (!updatedConfig[activeTab]) {
      updatedConfig[activeTab] = {};
    }
    if (!updatedConfig[activeTab][editingSection]) {
      updatedConfig[activeTab][editingSection] = {};
    }

    // 如果参数名改变了，先删除旧的
    if (editingParamKey !== editParam.key) {
      delete updatedConfig[activeTab][editingSection][editingParamKey];
    }

    // 转换值类型
    let value = editParam.value;
    if (editParam.type === 'number') {
      value = parseFloat(editParam.value) || 0;
    } else if (editParam.type === 'boolean') {
      value = editParam.value === 'true' || editParam.value === true;
    }

    updatedConfig[activeTab][editingSection][editParam.key] = {
      key: editParam.key,
      value: value,
      type: editParam.type,
      description: editParam.description,
      comment: editParam.comment
    };

    setCurrentConfig(updatedConfig);
    form.setFieldsValue(updatedConfig);
    setEditParamModalVisible(false);
    setEditParam({ key: '', value: '', type: 'string', description: '', comment: '' });

    // 保存参数注释到数据库
    try {
      if (gameTypeId) {
        const configFile = activeTab === 'gameUserSettings' ? 'GameUserSettings' : 'GameIni';
        
        await api.post(`/ark/parameter-comments`, {
          gameTypeId: gameTypeId,
          parameterKey: editParam.key,
          parameterComment: editParam.comment || '',
          parameterDescription: editParam.description || '',
          configSection: editingSection,
          configFile: configFile
        });
        console.log('✅ 参数注释保存成功');
      }
    } catch (error) {
      console.error('❌ 保存参数注释失败:', error);
      // 不显示错误消息，因为参数本身已经保存成功
    }

    message.success('参数编辑成功');
  };

  // 删除参数
  const deleteParameter = (section: string, key: string) => {
    const updatedConfig = { ...currentConfig };
    if (updatedConfig[activeTab] && updatedConfig[activeTab][section]) {
      delete updatedConfig[activeTab][section][key];
      setCurrentConfig(updatedConfig);
      form.setFieldsValue(updatedConfig);
      message.success('参数删除成功');
    }
  };

  // 更新参数值（优化版本，减少不必要的状态更新）
  const updateParameterValue = useCallback((section: string, key: string, value: any) => {
    setCurrentConfig((prevConfig: any) => {
      const updatedConfig = { ...prevConfig };
      
      // 只更新当前标签页的配置
      const currentTab = activeTab;
      if (updatedConfig[currentTab]?.[section]?.[key] !== undefined) {
        if (typeof updatedConfig[currentTab][section][key] === 'object' && updatedConfig[currentTab][section][key] !== null) {
          updatedConfig[currentTab][section][key] = {
            ...updatedConfig[currentTab][section][key],
            value: value
          };
        } else {
          updatedConfig[currentTab][section][key] = value;
        }
      }
      
      return updatedConfig;
    });
  }, [activeTab]);



  // 渲染参数输入控件（包装函数）
  const renderParameterInput = (section: string, param: Parameter) => {
    return <ParameterInput section={section} param={param} onUpdate={updateParameterValue} isAdmin={isAdmin} />;
  };

  // 添加常用参数（支持多选）
  const addCommonParameter = async () => {
    if (selectedParameters.length === 0) {
      message.error('请选择要添加的参数');
      return;
    }

    try {
      if (gameTypeId) {
        // 批量添加参数
        const addPromises = selectedParameters.map(parameterKey => {
          const param = availableParameters.find(p => p.key === parameterKey);
          if (param) {
            return api.post('/ark/common-parameters', {
              gameTypeId: gameTypeId,
              parameterKey: param.key,
              parameterComment: param.comment,
              parameterDescription: param.description,
              configSection: param.section,
              configFile: param.file
            });
          }
          return Promise.resolve();
        });

        await Promise.all(addPromises);
        
        // 重新加载常用参数
        await loadCommonParameters();
        
        setAddCommonParamModalVisible(false);
        setSelectedParameters([]);
        setNewCommonParam({
          parameterKey: '',
          parameterComment: '',
          parameterDescription: '',
          configSection: '',
          configFile: 'GameUserSettings'
        });
        
        message.success(`成功添加 ${selectedParameters.length} 个常用参数`);
      }
    } catch (error: any) {
      console.error('❌ 添加常用参数失败:', error);
      message.error(`添加常用参数失败: ${error.response?.data?.message || error.message}`);
    }
  };

  // 删除常用参数
  const deleteCommonParameter = async (parameterKey: string, configSection: string, configFile: string) => {
    try {
      if (gameTypeId) {
        await api.delete(`/ark/common-parameters/${gameTypeId}`, {
          data: {
            parameterKey,
            configSection,
            configFile
          }
        });
        
        // 重新加载常用参数
        await loadCommonParameters();
        
        message.success('常用参数删除成功');
      }
    } catch (error: any) {
      console.error('❌ 删除常用参数失败:', error);
      message.error(`删除常用参数失败: ${error.response?.data?.message || error.message}`);
    }
  };

  // 更新常用参数排序
  const updateCommonParameterSort = async (newOrder: CommonParameter[]) => {
    try {
      if (gameTypeId) {
        const parameters = newOrder.map((param, index) => ({
          parameterKey: param.parameter_key,
          configSection: param.config_section,
          configFile: param.config_file,
          sortOrder: index + 1
        }));
        
        await api.put('/ark/common-parameters/sort', {
          gameTypeId: gameTypeId,
          parameters: parameters
        });
        
        setCommonParameters(newOrder);
        message.success('排序更新成功');
      }
    } catch (error: any) {
      console.error('❌ 更新排序失败:', error);
      message.error(`更新排序失败: ${error.response?.data?.message || error.message}`);
    }
  };

  // 保存常用参数（仅管理员）
  const saveCommonParameters = async () => {
    try {
      if (gameTypeId && commonParameters.length > 0) {
        // 由于没有排序功能，这里只是重新加载常用参数列表
        // 实际上常用参数已经保存在数据库中了，不需要额外的保存操作
        await loadCommonParameters();
        
        message.success('常用参数配置已确认');
      } else {
        message.warning('没有常用参数需要保存');
      }
    } catch (error: any) {
      console.error('❌ 保存常用参数失败:', error);
      message.error(`保存常用参数失败: ${error.response?.data?.message || error.message}`);
    }
  };


  // 渲染简化的分组
  const renderSimpleSection = useCallback((sectionData: {key: string, section: any, paramCount: number, hasParams: boolean}) => {
    const { key: sectionKey, section, paramCount, hasParams } = sectionData;
    const isExpanded = expandedSections.has(sectionKey);
    
    return (
      <div key={sectionKey} className="ark-config-manager-section-wrapper">
        {/* 分组标题 */}
        <div 
          className={`ark-config-manager-section-header ${hasParams ? 'ark-config-manager-section-header-clickable' : ''}`}
          onMouseEnter={(e) => {
            if (hasParams) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (hasParams) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
            }
          }}
          onClick={() => hasParams && setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionKey)) {
              newSet.delete(sectionKey);
            } else {
              newSet.add(sectionKey);
            }
            return newSet;
          })}
        >
          <Space>
            <SettingOutlined className="ark-config-manager-section-icon" />
            <Text strong className="ark-config-manager-section-title">{section.name}</Text>
            <span className="ark-config-manager-badge">{paramCount}</span>
            {!hasParams && <Tag color="orange" className="ark-config-manager-tag">无参数</Tag>}
          </Space>
          <Space>
            {isAdmin && hasParams && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingSection(sectionKey);
                  setAddParamModalVisible(true);
                }}
                className="ark-config-manager-button"
              >
                添加参数
              </Button>
            )}
            {hasParams && (
              <Text className="ark-config-manager-section-hint">
                {isExpanded ? '点击收起' : '点击展开'}
              </Text>
            )}
          </Space>
        </div>
        
        {/* 参数列表 */}
        {isExpanded && hasParams && (
          <div className="ark-config-manager-section-content">
            {flattenedParams
              .filter(p => p.sectionKey === sectionKey)
              .map(({ paramKey, param }) => (
                <div 
                  key={paramKey} 
                  className="ark-config-manager-parameter-item"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                    e.currentTarget.style.borderColor = '#4facfe';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(79, 172, 254, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(248, 250, 252, 0.8)';
                    e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="ark-config-manager-parameter-name">
                    <Text strong className="ark-config-manager-parameter-title">
                      {param.comment || paramKey}
                    </Text>
                    {param.comment && (
                      <Text type="secondary" className="ark-config-manager-parameter-key">
                        ({paramKey})
                      </Text>
                    )}
                    {!isAdmin && ['RCONPort', 'MaxPlayers', 'Port', 'QueryPort'].includes(paramKey) && (
                      <Tag color="orange" className="ark-config-manager-readonly-tag">
                        只读
                      </Tag>
                    )}
                    {param.description && (
                      <Tooltip title={param.description}>
                        <InfoCircleOutlined className="ark-config-manager-info-icon" />
                      </Tooltip>
                    )}
                  </div>
                  <div className="ark-config-manager-parameter-input">
                    {renderParameterInput(sectionKey, param)}
                  </div>
                  <div className="ark-config-manager-parameter-actions">
                    {isAdmin && (
                      <>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          size="small"
                          className="ark-config-manager-button"
                          onClick={() => editParameter(sectionKey, paramKey)}
                          title="编辑参数"
                        />
                        <Popconfirm
                          title="确定要删除这个参数吗？"
                          onConfirm={() => deleteParameter(sectionKey, paramKey)}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            className="ark-config-manager-button"
                            title="删除参数"
                          />
                        </Popconfirm>
                      </>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
        
        {isExpanded && !hasParams && (
          <div className="ark-config-manager-empty-state">
            <div className="ark-config-manager-empty-icon">
              <SettingOutlined />
            </div>
            <Text className="ark-config-manager-empty-text">
              {isAdmin ? '暂无参数，点击"添加参数"开始配置' : '暂无参数'}
            </Text>
          </div>
        )}
      </div>
    );
  }, [expandedSections, isAdmin, flattenedParams, renderParameterInput, deleteParameter, setEditingSection, setAddParamModalVisible]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // 并行加载配置结构和常用参数，然后加载服务器配置
        await Promise.all([
          loadConfigStructure(),
          loadCommonParameters()
        ]);
        // 最后加载服务器配置
        await loadServerConfig();
      } catch (error) {
        console.error('加载数据失败:', error);
        message.error('加载数据失败');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [serverId]);

  // 当配置结构加载完成后，加载可用参数列表
  useEffect(() => {
    if (mergedConfigStructure) {
      loadAvailableParameters();
    }
  }, [mergedConfigStructure]);


  if (loading) {
    return (
      <div className="ark-config-manager-loading">
        <Spin size="large" />
        <div className="ark-config-manager-loading-text">加载ARK配置中...</div>
      </div>
    );
  }

  if (!configStructure) {
    return (
      <Alert
        message="配置结构加载失败"
        description="无法加载ARK配置结构，请检查网络连接"
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className="ark-config-manager-container">
      <Card className="ark-config-manager-main-card" styles={{ body: { padding: '0' } }}>
        {/* 自定义头部 */}
        <div className="ark-config-manager-header">
          <div className="ark-config-manager-header-content">
            <div>
              <Title level={3} className="ark-config-manager-title">
                <FileTextOutlined className="ark-config-manager-title-icon" />
                ARK服务器配置管理
              </Title>
              <Text className="ark-config-manager-subtitle">
                悠悠鸟游戏针对ARK定制开发的游戏参数设置工具。
              </Text>
            </div>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadServerConfig}
                loading={loading}
                className="ark-config-manager-header-button"
              >
                重新加载
              </Button>
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  generateCommandPreview(currentConfig);
                  generateConfigFilesPreview(currentConfig);
                }}
                loading={loading}
                className="ark-config-manager-header-button"
              >
                生成预览
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={saveConfig}
                loading={saving}
                className="ark-config-manager-header-button-primary"
              >
                保存配置
              </Button>
            </Space>
          </div>
        </div>

        <div className="ark-config-manager-content">

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setExpandedSections(new Set()); // 切换标签页时折叠所有分组
          }}
          className="ark-config-manager-tabs"
          tabBarStyle={{
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            padding: '0 16px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
          items={[
            {
              key: 'gameUserSettings',
              label: 'GameUserSettings.ini',
              children: (
                <div>
                  {/* 常用参数区域 */}
                  {showCommonParameters && (
                    <Card 
                      title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>常用设置</span>
                          <Space>
                            <Button 
                              size="small" 
                              icon={<PlusOutlined />}
                              onClick={() => setAddCommonParamModalVisible(true)}
                              className="ark-config-manager-button"
                            >
                              添加参数
                            </Button>
                            {isAdmin && (
                              <Button 
                                size="small" 
                                onClick={saveCommonParameters}
                                className="ark-config-manager-button"
                              >
                                保存常用参数
                              </Button>
                            )}
                            <Button 
                              size="small" 
                              icon={showCommonParameters ? <EyeOutlined /> : <SettingOutlined />}
                              onClick={() => setShowCommonParameters(!showCommonParameters)}
                              className="ark-config-manager-button"
                            >
                              {showCommonParameters ? '隐藏' : '显示'}
                            </Button>
                          </Space>
                        </div>
                      }
                      style={{ marginBottom: '20px' }}
                    >
                      <Alert
                        message="常用设置"
                        description="您可以在这里添加和管理最常用的参数，方便快速配置。参数值会与下面的配置项同步。"
                        type="info"
                        showIcon
                        style={{ marginBottom: '16px' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {commonParameters.map((param, index) => {
                          const configKey = param.config_file === 'GameUserSettings' ? 'gameUserSettings' : 'gameIni';
                          const paramValue = currentConfig[configKey]?.[param.config_section]?.[param.parameter_key];
                          
                          // 处理常用参数
                          
                          return (
                            <div key={`${param.parameter_key}-${param.config_section}-${param.config_file}`} 
                                 style={{ 
                                   display: 'flex',
                                   alignItems: 'center',
                                   gap: '16px',
                                   border: '1px solid #d9d9d9',
                                   borderRadius: '6px',
                                   padding: '12px',
                                   backgroundColor: '#fafafa'
                                 }}>
                              {/* 参数名称和标签 */}
                              <div style={{ minWidth: '150px' }}>
                                <div style={{ marginBottom: '4px' }}>
                                  <Text strong style={{ fontSize: '14px' }}>
                                    {(param.parameter_comment && param.parameter_comment !== '0') ? param.parameter_comment : param.parameter_key}
                                  </Text>
                                  {param.is_default === 1 && (
                                    <Tag color="blue" style={{ marginLeft: '8px', fontSize: '12px' }}>
                                      默认
                                    </Tag>
                                  )}
                                </div>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  {param.parameter_description}
                                </Text>
                              </div>
                              
                              {/* 参数输入控件 */}
                              <div style={{ flex: 1, minWidth: '200px' }}>
                                {paramValue !== undefined && paramValue !== null ? (
                                  <ParameterInput 
                                    section={param.config_section} 
                                    param={{
                                      key: param.parameter_key,
                                      value: paramValue.value !== undefined ? paramValue.value : paramValue,
                                      type: typeof (paramValue.value !== undefined ? paramValue.value : paramValue) === 'boolean' ? 'boolean' : 
                                            typeof (paramValue.value !== undefined ? paramValue.value : paramValue) === 'number' ? 'number' : 'string',
                                      description: param.parameter_description,
                                      comment: param.parameter_comment
                                    }} 
                                    onUpdate={updateParameterValue}
                                    isAdmin={isAdmin}
                                  />
                                ) : (
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    参数未找到
                                  </Text>
                                )}
                              </div>
                              
                              {/* 操作按钮 */}
                              <div style={{ minWidth: '60px', textAlign: 'right' }}>
                                <Popconfirm
                                  title="确定要从常用参数中移除这个参数吗？"
                                  onConfirm={() => deleteCommonParameter(param.parameter_key, param.config_section, param.config_file)}
                                  okText="确定"
                                  cancelText="取消"
                                >
                                  <Button size="small" type="text" style={{ color: '#52c41a' }}>
                                    移除常用
                                  </Button>
                                </Popconfirm>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  )}
                  
                  {/* 搜索和操作栏 */}
                  <div className="ark-config-manager-search-bar">
                    <div className="ark-config-manager-search-content">
                      <AntInput.Search
                        placeholder="搜索参数名称或描述..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="ark-config-manager-search-input"
                        allowClear
                        size="large"
                      />
                      <Space>
                        <Button 
                          size="small" 
                          onClick={() => setExpandedSections(new Set(filteredSections.map(s => s.key)))}
                          className="ark-config-manager-button"
                        >
                          展开全部
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => setExpandedSections(new Set())}
                          className="ark-config-manager-button"
                        >
                          折叠全部
                        </Button>
                        <Button 
                          size="small" 
                          type={showOnlyWithParams ? 'primary' : 'default'}
                          onClick={() => setShowOnlyWithParams(!showOnlyWithParams)}
                          className="ark-config-manager-button"
                        >
                          {showOnlyWithParams ? '显示所有分组' : '只显示有参数的分组'}
                        </Button>
                      </Space>
                    </div>
                  </div>
                  
                  {/* 简化的分组列表 */}
                  {filteredSections.map(sectionData => renderSimpleSection(sectionData))}
                </div>
              )
            },
            {
              key: 'gameIni',
              label: 'Game.ini',
              children: (
                <div>
                  {/* 搜索和操作栏 */}
                  <div className="ark-config-manager-search-bar">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <AntInput.Search
                        placeholder="搜索参数名称或描述..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="ark-config-manager-search-input"
                        allowClear
                        size="large"
                      />
                      <Space>
                        <Button 
                          size="small" 
                          onClick={() => setExpandedSections(new Set(filteredSections.map(s => s.key)))}
                          className="ark-config-manager-button"
                        >
                          展开全部
                        </Button>
                        <Button 
                          size="small" 
                          onClick={() => setExpandedSections(new Set())}
                          className="ark-config-manager-button"
                        >
                          折叠全部
                        </Button>
                        <Button 
                          size="small" 
                          type={showOnlyWithParams ? 'primary' : 'default'}
                          onClick={() => setShowOnlyWithParams(!showOnlyWithParams)}
                          className="ark-config-manager-button"
                        >
                          {showOnlyWithParams ? '显示所有分组' : '只显示有参数的分组'}
                        </Button>
                      </Space>
                    </div>
                  </div>
                  
                  {/* 简化的分组列表 */}
                  {filteredSections.map(sectionData => renderSimpleSection(sectionData))}
                </div>
              )
            },
            {
              key: 'preview',
              label: '配置文件预览',
              children: (
                <div>
                  <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <Button
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        generateConfigFilesPreview(currentConfig);
                      }}
                      loading={loading}
                      className="ark-config-manager-button"
                    >
                      生成预览
                    </Button>
                  </div>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Card title="GameUserSettings.ini" size="small">
                        <pre className="ark-config-manager-preview-code">
                          {configFilesPreview.gameUserSettings || '暂无内容'}
                        </pre>
                      </Card>
                    </Col>
                    <Col span={12}>
                      <Card title="Game.ini" size="small">
                        <pre className="ark-config-manager-preview-code">
                          {configFilesPreview.gameIni || '暂无内容'}
                        </pre>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )
            }
          ]}
        />
        </div>
      </Card>

      {/* 添加参数模态框 */}
      <Modal
        title="添加新参数"
        open={addParamModalVisible}
        onOk={addParameter}
        onCancel={() => setAddParamModalVisible(false)}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="参数名称" required>
            <Input
              value={newParam.key}
              onChange={(e) => setNewParam({ ...newParam, key: e.target.value })}
              placeholder="例如: SessionName"
            />
          </Form.Item>
          <Form.Item label="参数类型" required>
            <Select
              value={newParam.type}
              onChange={(value) => setNewParam({ ...newParam, type: value })}
            >
              <Select.Option value="string">字符串 (String)</Select.Option>
              <Select.Option value="number">数字 (Number)</Select.Option>
              <Select.Option value="boolean">布尔值 (Boolean)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="参数值" required>
            {newParam.type === 'boolean' ? (
              <Select
                value={newParam.value}
                onChange={(value) => setNewParam({ ...newParam, value })}
              >
                <Select.Option value={true}>True</Select.Option>
                <Select.Option value={false}>False</Select.Option>
              </Select>
            ) : newParam.type === 'number' ? (
              <InputNumber
                value={newParam.value}
                onChange={(value) => setNewParam({ ...newParam, value })}
                className="ark-config-manager-full-width"
              />
            ) : (
              <Input
                value={newParam.value}
                onChange={(e) => setNewParam({ ...newParam, value: e.target.value })}
                placeholder="请输入参数值"
              />
            )}
          </Form.Item>
          <Form.Item label="参数描述">
            <Input.TextArea
              value={newParam.description}
              onChange={(e) => setNewParam({ ...newParam, description: e.target.value })}
              placeholder="可选：描述这个参数的用途"
              rows={3}
            />
          </Form.Item>
          <Form.Item label="参数注释">
            <Input
              value={newParam.comment}
              onChange={(e) => setNewParam({ ...newParam, comment: e.target.value })}
              placeholder="可选：中文注释，用于显示替代参数名"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑参数模态框 */}
      <Modal
        title="编辑参数"
        open={editParamModalVisible}
        onOk={saveEditParameter}
        onCancel={() => setEditParamModalVisible(false)}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="参数名称" required>
            <Input
              value={editParam.key}
              onChange={(e) => setEditParam({ ...editParam, key: e.target.value })}
              placeholder="例如: SessionName"
            />
          </Form.Item>
          <Form.Item label="参数类型" required>
            <Select
              value={editParam.type}
              onChange={(value) => setEditParam({ ...editParam, type: value })}
            >
              <Select.Option value="string">字符串 (String)</Select.Option>
              <Select.Option value="number">数字 (Number)</Select.Option>
              <Select.Option value="boolean">布尔值 (Boolean)</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="参数值" required>
            {editParam.type === 'boolean' ? (
              <Select
                value={editParam.value}
                onChange={(value) => setEditParam({ ...editParam, value })}
              >
                <Select.Option value={true}>True</Select.Option>
                <Select.Option value={false}>False</Select.Option>
              </Select>
            ) : editParam.type === 'number' ? (
              <InputNumber
                value={editParam.value}
                onChange={(value) => setEditParam({ ...editParam, value })}
                className="ark-config-manager-full-width"
              />
            ) : (
              <Input
                value={editParam.value}
                onChange={(e) => setEditParam({ ...editParam, value: e.target.value })}
                placeholder="请输入参数值"
              />
            )}
          </Form.Item>
          <Form.Item label="参数描述">
            <Input.TextArea
              value={editParam.description}
              onChange={(e) => setEditParam({ ...editParam, description: e.target.value })}
              placeholder="可选：描述这个参数的用途"
              rows={3}
            />
          </Form.Item>
          <Form.Item label="参数注释">
            <Input
              value={editParam.comment}
              onChange={(e) => setEditParam({ ...editParam, comment: e.target.value })}
              placeholder="可选：中文注释，用于显示替代参数名"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 添加常用参数模态框 */}
      <Modal
        title="添加常用参数"
        open={addCommonParamModalVisible}
        onOk={addCommonParameter}
        onCancel={() => {
          setAddCommonParamModalVisible(false);
          setSelectedParameters([]);
        }}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item label="选择参数" required>
            <Select
              mode="multiple"
              showSearch
              placeholder="请选择要添加的参数（支持多选）"
              value={selectedParameters}
              onChange={(values) => {
                setSelectedParameters(values);
              }}
              filterOption={(input, option) => {
                const param = availableParameters.find(p => p.key === option?.value);
                return param ? 
                  param.comment.toLowerCase().includes(input.toLowerCase()) ||
                  param.key.toLowerCase().includes(input.toLowerCase()) ||
                  param.description.toLowerCase().includes(input.toLowerCase())
                  : false;
              }}
              maxTagCount="responsive"
              style={{ width: '100%' }}
            >
              {availableParameters.map(param => {
                // 检查参数是否已经添加到常用参数中
                const isAlreadyAdded = commonParameters.some(commonParam => 
                  commonParam.parameter_key === param.key && 
                  commonParam.config_section === param.section && 
                  commonParam.config_file === param.file
                );
                
                return (
                  <Select.Option key={param.key} value={param.key} disabled={isAlreadyAdded}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {param.comment} ({param.key})
                          {isAlreadyAdded && (
                            <Tag color="green">已添加</Tag>
                          )}
                        </div>
                        <div style={{ color: '#666', marginTop: '4px' }}>
                          {param.description} - {param.file} / {param.section}
                        </div>
                      </div>
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          {selectedParameters.length > 0 && (
            <Form.Item label="已选择的参数">
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: '6px', padding: '8px' }}>
                {selectedParameters.map(parameterKey => {
                  const param = availableParameters.find(p => p.key === parameterKey);
                  return param ? (
                    <div key={parameterKey} style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {param.comment} ({param.key})
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {param.description} - {param.file} / {param.section}
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ARKConfigManager;